import { EyeOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Badge, Button, Card, DatePicker, Form, Input, Select, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import CopyableText from '../../components/copyable-text';
import DeviceDetails from '../../components/device-details';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { Device } from '../../types/device.types';
import { RetailerMinimal } from '../../types/retailers.types';
import { UserMinimal } from '../../types/user.types';
import { formatNumber, handleAPIError } from '../../utils/helpers';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface FilterValues {
	status: string | null;
	phone_number: string | null;
	user: number | null;
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const RetailerDevices = () => {
	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({
		page: 1,
		pageSize: 10,
	});
	const [count, setCount] = useState(0);
	const [filters, setFilters] = useState<FilterValues>({
		status: null,
		phone_number: null,
		user: null,
		date_range: null,
	});
	const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

	const { data, error, isLoading } = useQuery({
		queryKey: ['devices', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const params = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
				status: filters.status,
				phone_number: filters.phone_number,
				user: filters.user,
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
			};

			const response = await axios.get('/retailers/devices', {
				withCredentials: true,
				params,
			});
			setCount(response.data.count);
			return response.data;
		},
	});

	if (error) {
		handleAPIError(error);
	}

	const devices: Device[] = data?.results || [];

	const getStatusColor = (status: Device['status']) => {
		switch (status) {
			case 'pending':
				return 'green';
			case 'approved':
				return 'blue';
			case 'rejected':
				return 'red';
			default:
				return 'default';
		}
	};

	const columns: ColumnsType<Device> = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'User',
			dataIndex: 'user',
			key: 'user',
			render: (user: UserMinimal) => (
				<div className="flex items-center gap-2">
					<Avatar src={user.dp} />
					<div>
						<span className="text-sm font-medium uppercase">
							{user.first_name} {user.last_name}
						</span>
						<CopyableText text={user.phone_number}>
							<a
								className="text-sm text-gray-500"
								href={`tel:+${user.phone_number}`}
							>
								{user.phone_number}
							</a>
						</CopyableText>
					</div>
				</div>
			),
		},
		{
			title: 'Phone Model',
			dataIndex: 'phone_model',
			key: 'phone_model',
		},
		{
			title: 'Retailer',
			dataIndex: 'retailer',
			key: 'retailer',
			render: (retailer: RetailerMinimal) => (
				<div>
					<span className="text-sm font-medium uppercase">{retailer.business_name}</span>
					<CopyableText
						text={retailer.vendor_id}
						className="text-sm text-gray-500"
					/>
				</div>
			),
		},
		{
			title: 'Phone Amount',
			dataIndex: 'phone_amount',
			key: 'phone_amount',
			render: (phone_amount: string) => `${formatNumber(phone_amount)} UGX`,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status: string) => <Tag color={getStatusColor(status as Device['status'])}>{status.toUpperCase()}</Tag>,
		},
		{
			title: 'Payment Plan',
			dataIndex: 'payment_plan',
			key: 'payment_plan',
			render: (payment_plan: string, device: Device) => (
				<div className="flex flex-col gap-2">
					<span className="text-sm font-medium uppercase">Plan: {payment_plan}</span>
					<span className="text-sm text-gray-500">
						Duration: {device.duration} {device.duration_unit}
					</span>
				</div>
			),
		},
		{
			title: 'Payment Progress',
			dataIndex: 'total_amount_to_be_paid',
			key: 'total_amount_to_be_paid',
			render: (total_amount_to_be_paid: string, device: Device) =>
				device.status === 'approved' || device.status === 'pending' ? (
					<div className="flex flex-col gap-2">
						<span className="text-sm font-medium uppercase">Total: {formatNumber(total_amount_to_be_paid)} UGX</span>
						<span className="text-sm text-gray-500">Total Paid: {formatNumber(device.total_amount_paid)} UGX</span>
						<span className="text-sm text-gray-500">
							Remaining: {formatNumber(parseFloat(total_amount_to_be_paid) - parseFloat(device.total_amount_paid))} UGX
						</span>
						<span className="text-sm text-gray-500">
							<Badge
								color="green"
								count={device.number_of_installments_paid}
								showZero
								size="small"
							/>{' '}
							of {device.number_of_installments} Installments Paid
						</span>
					</div>
				) : (
					<span>N/A</span>
				),
		},
		{
			title: 'Actions',
			dataIndex: 'id',
			key: 'id',
			render: (_: number, device: Device) => (
				<div className="flex flex-col gap-2">
					<Button
						type="link"
						onClick={() => setSelectedDevice(device)}
						icon={<EyeOutlined />}
					>
						View
					</Button>
				</div>
			),
		},
	];

	const handleFilterApply = (values: FilterValues) => {
		setFilters(values);
		setPaginationConfig({
			...paginationConfig,
			page: 1, // Reset to first page when filtering
		});
	};

	const handleFilterReset = () => {
		form.resetFields();
		setFilters({
			status: null,
			phone_number: null,
			user: null,
			date_range: null,
		});
		setPaginationConfig({
			...paginationConfig,
			page: 1,
		});
	};

	const statusOptions = [
		{ value: 'pending', label: 'Pending' },
		{ value: 'approved', label: 'Approved' },
		{ value: 'rejected', label: 'Rejected' },
	];

	return (
		<AppLayout
			title="Devices"
			subtitle="Manage devices and their contracts."
		>
			<Card className="mb-4 shadow-sm">
				<Form
					form={form}
					layout="horizontal"
					onFinish={handleFilterApply}
					initialValues={filters}
				>
					<div className="flex flex-wrap items-end gap-4">
						<Form.Item
							name="phone_number"
							label="Phone Number"
							className="mb-0 min-w-[180px]"
						>
							<Input
								placeholder="Enter phone number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="user"
							label="User ID"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="User ID"
								type="number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="status"
							label="Status"
							className="mb-0 min-w-[150px]"
						>
							<Select
								placeholder="Select status"
								allowClear
								options={statusOptions}
							/>
						</Form.Item>

						<Form.Item
							name="date_range"
							label="Date Range"
							className="mb-0 min-w-[300px]"
						>
							<RangePicker
								allowClear
								format="YYYY-MM-DD"
								placeholder={['Start Date', 'End Date']}
							/>
						</Form.Item>

						<Form.Item className="mb-0 flex-shrink-0">
							<Space>
								<Button
									type="primary"
									htmlType="submit"
									icon={<FilterOutlined />}
								>
									Apply Filters
								</Button>
								<Button
									onClick={handleFilterReset}
									icon={<ReloadOutlined />}
								>
									Reset
								</Button>
							</Space>
						</Form.Item>
					</div>
				</Form>
			</Card>

			<div className="mb-20">
				<Table
					columns={columns}
					dataSource={devices}
					rowKey="id"
					pagination={{
						total: count,
						pageSize: paginationConfig.pageSize,
						current: paginationConfig.page,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						onChange: (page, pageSize) => {
							setPaginationConfig({ page, pageSize });
						},
					}}
					loading={isLoading}
				/>
			</div>

			{selectedDevice && (
				<DeviceDetails
					device={selectedDevice}
					onClose={() => setSelectedDevice(null)}
				/>
			)}
		</AppLayout>
	);
};

export default RetailerDevices;
